import { Controller } from 'stimulus'

import Rails from '@rails/ujs'

// importando dessa forma para tree-shake
// INFO: https://stackoverflow.com/questions/58741044/why-webpack-doesnt-tree-shake-the-lodash-when-using-import-as
import throttle from 'lodash/throttle'
import debounce from 'lodash/debounce'

const KEY_CODES = {
  escape: 27
}

export class BaseSearchController extends Controller {
  static targets = ['search', 'results']

  initialize() {
    this.search = debounce(throttle(this.search, 750), 300)
    this._cachedResults = {}

    this._setLoading(false)

    this._currentPage = this.element.getAttribute('data-current-page')

    this._searchTerm = this.searchTarget.value

    this._updateHasContentStatus()

    if (this._searchTerm) {
      // move o cursor para o final do texto para permitir que o usuário complemente sua
      // busca. (ex: usuário buscou na home e apertou enter. deve cair em
      // candidates pronto para continuar digitanto)

      setTimeout(() => { this.searchTarget.selectionStart = this.searchTarget.selectionEnd = this.searchTarget.value.length }, 0)

      this.search()
    }

    // paginação vem via ajax, o resto via turbo
    this.element.addEventListener('ajax:success', (aEvent) => {

    })

    // temos que atualizar a url (página) manualmente pois a paginação
    // é remota (ajax)
    this.element.addEventListener('ajax:before', aEvent => this._onAjaxBefore(aEvent))

    window.addEventListener('popstate', aEvent => this._onPopState(aEvent))

  }

  // Stimulus event listeners

  onKeydown(aEvent) {
    this._onKeyDown(aEvent)
  }

  onInput(aEvent) {
    this._onInput(aEvent)
  }

  onCancelClick(aEvent) {
    this._onCancelClick(aEvent)
  }

  // Properties

  get fromParam() {
    return 'home'
  }

  get currentPage() {
    return this._currentPage || 1
  }

  get searchParams() {
    return {
      from: this.fromParam,
      search: this.searchTerm,
      p: this.currentPage
    }
  }

  get searchTerm() {
    return this._searchTerm
  }

  get searchUrl() {
    return this.element.getAttribute('data-url')
  }

  get cacheKey() {
    return `${this.searchTerm.trim()}/${this.currentPage}`
  }

  // Public methods

  search() {
    if (this._cachedResults[this.cacheKey]) {
      this._showCachedResults()
      return
    }

    if (this.searchTerm.trim() === '') {
      this._clearResults()
      return
    }

    if (this._isLoading) {
      // no retorno do que está sendo carregado é verificado se o termo de
      // busca mudou e faz nova busca caso tenha mudado.

      this._nextSearch = this.searchTerm

      return
    }

    // já tem próxima busca agendada que é tratada no fecthResults
    if (this._nextSearch) {
      return
    }

    this._fetchResults(this.searchTerm)
  }

  /* Privates */

  _fetchResults(searchTerm) {
    const params = this.searchParams,
          oldSearchTerm = this.searchTerm,
          data = new URLSearchParams(params).toString(),
          cacheKey = this.cacheKey

    this._setLoading(true)

    Rails.ajax({
      url: this.searchUrl,
      data: data,
      type: "GET",
      complete: (xhr, status) => {

        // A busca pode ter sido 'limpa' enquando esperávamos o resultado.
        // não exibimos o resultado nesses casos.
        const wasCleared = (! this._isLoading)

        this._setLoading(false)

        if (status === 'OK') {
          this._cachedResults[cacheKey] = xhr.responseText

          if (! wasCleared) {
            this._showResults(this._cachedResults[cacheKey])
          }

          if (this._nextSearch) {
            this._searchTerm = this._nextSearch
            this._nextSearch = null

            // faz nova busca para o valor atual.
            this.search()
          }
        }
      }
    })
  }

  _showResults(aData) {
    this.resultsTarget.innerHTML = aData || ''
  }

  _showCachedResults() {
    this._setLoading(true)

    setTimeout( () => {
      this._setLoading(false)
      this._nextSearch = null
      this._showResults(this._cachedResults[this.cacheKey])
      this._updateUrl()
    }, 80 )
  }

  _clearResults() {
    this._nextSearch = null
    this._currentPage = '1'
    this._setLoading(false)
    this._showResults(null)
    this._updateHasContentStatus()
    this._updateUrl()
  }

  _setLoading(aIsLoading) {
    this._isLoading = aIsLoading
    this.element.setAttribute('data-loading', aIsLoading)
  }

  _updateUrl() {
    const params = this.searchParams,
          data = new URLSearchParams(params).toString(),
          state = { confirma: { params: params } }

    if (history.state.turbo) {
      // não sobrescrevemos o state do turbo para não perder o back
      history.pushState(state, document.title, `?${data}`)
    } else {
      history.replaceState(state, document.title, `?${data}`)
    }
  }

  _getParamsFromUrl(aUrl) {
    const pairs = aUrl.slice(1).split('&'),
          result = {}

    pairs.forEach((aPair) => {
      const splitted = aPair.split('=')

      result[splitted[0]] = decodeURIComponent(splitted[1] || '')
    })

    return JSON.parse(JSON.stringify(result));
  }

  _updateHasContentStatus() {
    this.element.setAttribute('data-has-content', (this._searchTerm !== ''))
  }

  _clearSearch() {
    this.searchTarget.value = ''
    this._searchTerm = ''

    this._clearResults()

    this.searchTarget.focus()
  }

  // Event handlers

  _onKeyDown(aEvent) {
    if (aEvent.keyCode === KEY_CODES['escape']) {
      this._clearSearch()
    }
  }

  _onInput(aEvent) {
    this._searchTerm = this.searchTarget.value

    // precisamos limpar a página quando já uma nova busca. pois o usuário
    // pode estar no final e o novo filtro nem ter essa página.

    this._currentPage = '1'

    this._updateUrl()

    this._updateHasContentStatus()

    this.search()
  }

  _onCancelClick(aEvent) {
    aEvent.preventDefault()
    this._clearSearch()
  }

  _onAjaxBefore(aEvent) {
    const isPaginationLink =
      aEvent.target.closest('nav').classList.contains('pagination')

    if (isPaginationLink) {
      const url = aEvent.target.href,
            params = this._getParamsFromUrl(url),
            page = params['p']

      this._currentPage = page
      this._updateUrl()

      // vamos tratar direto no 'search()' para centralizar os mecanismos
      // de cache/wait.
      aEvent.preventDefault()

      this.search()
    }
  }

  _onPopState(aEvent) {
    if (aEvent.state && aEvent.state.confirma) {
      window.location.reload()
    }
  }
}
